import { DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from "@dnd-kit/core"
import { restrictToVerticalAxis } from "@dnd-kit/modifiers"
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { Divider, TableColumnType } from "antd"
import React, { useCallback } from "react"
import { useTranslation } from "react-i18next"
import { PurchaseOrderColumns } from "../columns/PurchaseOrderColumns"
import { DraggableColumnItem } from "./DragableColumnItem"

interface DraggableColumnMenuProps<T> {
    allColumns: Array<TableColumnType<T>>
    visibleColumns: Array<string>
    columnOrder: Array<string>
    onToggleColumn: (key: string) => void
    onReorderColumns?: (newOrder: Array<string>) => void
    i18nColumnBasePath: string
}

export const DraggableColumnMenu = <T,>({ allColumns, visibleColumns, columnOrder, onToggleColumn, onReorderColumns, i18nColumnBasePath }: DraggableColumnMenuProps<T>) => {
    const { t } = useTranslation()

    // Create a map for quick column lookup
    const columnMap = new Map(allColumns.map((col) => [col.key as string, col]))

    const orderedColumns = columnOrder.map((key) => columnMap.get(key)).filter(Boolean)

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 1,
            },
        }),
    )

    const handleDragEnd = useCallback(
        (event: DragEndEvent) => {
            const { active, over } = event

            if (!over || active.id === over.id) {
                return
            }
            // Prevent dragging over invisible/disabled items
            const isOverColumnVisible = visibleColumns.includes(over.id as string)
            const isOverColumnDisabled = over.id === PurchaseOrderColumns.NAME || over.id === PurchaseOrderColumns.ACTION

            if (!isOverColumnVisible || isOverColumnDisabled) {
                return
            }

            const activeIndex = columnOrder.findIndex((id) => id === active.id)
            const overIndex = columnOrder.findIndex((id) => id === over.id)

            if (activeIndex !== -1 && overIndex !== -1) {
                const newOrder = [...columnOrder]
                const [removed] = newOrder.splice(activeIndex, 1)
                newOrder.splice(overIndex, 0, removed)

                onReorderColumns?.(newOrder)
            }
        },
        [columnOrder, onReorderColumns],
    )

    if (!orderedColumns) {
        return null
    }

    return (
        <div className="p-16 bg-white shadow-popover">
            <div className="text-xs font-medium   flex flex-row justify-between items-center p-8">
                <span className="text-sm font-medium">{t("purchaseOrders:columns.columns")}</span>
                <span className="text-sm font-medium ">
                    {visibleColumns.length} / {allColumns.length}
                </span>
            </div>
            <Divider className="my-2" />

            <DndContext sensors={sensors} modifiers={[restrictToVerticalAxis]} onDragEnd={handleDragEnd}>
                <SortableContext items={orderedColumns.map((col) => col!.key as string)} strategy={verticalListSortingStrategy}>
                    <div className="flex flex-col mt-16">
                        {orderedColumns.map((col) => (
                            <DraggableColumnItem
                                key={col!.key}
                                column={col!}
                                isVisible={visibleColumns.includes(col!.key as string)}
                                onToggle={onToggleColumn}
                                disabled={col!.key === PurchaseOrderColumns.NAME || col!.key === PurchaseOrderColumns.ACTION}
                                i18nColumnBasePath={i18nColumnBasePath}
                            />
                        ))}
                    </div>
                </SortableContext>
            </DndContext>
        </div>
    )
}
